import { useMemo } from 'react';
import { FormatterType } from '@fabric-ui/components';
import useQueryObject from '@hooks/useQueryObjectSql';
import { keepPreviousData } from '@tanstack/react-query';
import { and, field, or } from '@utils/filter';
import { DwQueryRequest } from '@views/CustomDashboards/types';
import { MappingTreeNode } from '@views/Sources/Mapping/types';
import { normalizePath, splitPath } from '@views/Sources/Mapping/utils';

const typeToFormatterResolver = {
  INTEGER: FormatterType.number,
  Number: FormatterType.number,
  BOOLEAN: FormatterType.boolean,
  Array: FormatterType.array,
  Object: FormatterType.json,
  VARCHAR: FormatterType.string,
  Text: FormatterType.string,
};

// Builds a tree from a flat list of DOCUMENT_KEYS paths. Segments carrying the
// V2 `[*]` marker are folded into their parent name (so mapping values keep
// matching `nested.key`) but tagged as arrays on the tree node so consumers
// like `toExplodedPath` and the drag-and-drop UI can render them correctly.
// V1 keys have no `[*]` and pass through as plain object segments.
const buildTreeFromKeyList = (keys: string[], fieldTypes: Array<{ path: string; type: string }> = []): MappingTreeNode[] => {
  interface RawNode {
    isArray: boolean;
    children: Record<string, RawNode>;
  }
  const root: Record<string, RawNode> = {};

  keys.forEach(key => {
    splitPath(key).reduce((currentChildren, { name, isArray }) => {
      const existing = currentChildren[name];
      const node: RawNode = existing ?? { isArray: false, children: {} };
      // A path is tagged as an array boundary if any incoming key marks it as one.
      node.isArray = node.isArray || isArray;
      // eslint-disable-next-line no-param-reassign
      currentChildren[name] = node;
      return node.children;
    }, root);
  });

  const convertToNodeArray = (nodes: Record<string, RawNode>, prefix = ''): MappingTreeNode[] =>
    Object.entries(nodes).map(([name, raw]) => {
      const currentPath = prefix ? `${prefix}.${name}` : name;
      const children = convertToNodeArray(raw.children, currentPath);
      const describedType = fieldTypes.find(({ path }) => path === currentPath)?.type;
      const mappedType = describedType ? typeToFormatterResolver[describedType] : null;
      // `[*]` in the key string is the authoritative signal for array-of-objects;
      // DESCRIBE_JSON only comes into play for scalar typing (Number/Boolean/Date).
      const type = raw.isArray ? FormatterType.array : mappedType;

      return {
        label: name,
        path: currentPath,
        children,
        isMissing: false,
        ...(type ? { type } : {}),
      };
    });

  return convertToNodeArray(root);
};

export const buildKeyTree = (
  data: Record<string, any> | string[],
  fieldTypes: Array<{ path: string; type: string }> = [],
  prefix = ''
): MappingTreeNode[] => {
  if (Array.isArray(data) && data.length > 0 && typeof data[0] === 'string') {
    return buildTreeFromKeyList(data as string[], fieldTypes);
  }

  if (!data || typeof data !== 'object' || Array.isArray(data)) {
    return [];
  }

  return Object.keys(data).map(key => {
    const value = data[key];
    const currentPath = prefix ? `${prefix}.${key}` : key;
    const children = Array.isArray(value) ? [] : buildKeyTree(value, fieldTypes, currentPath);
    const fieldType = fieldTypes.find(({ path }) => path === currentPath)?.type;
    const type = fieldType ? typeToFormatterResolver[fieldType] : null;

    return {
      label: key,
      path: currentPath,
      children,
      isMissing: false,
      ...(type ? { type } : {}),
    };
  });
};

export const useMappingFileFields = ({
  instanceId,
  workflowId,
  enabled,
}: {
  instanceId?: string;
  workflowId?: string;
  enabled?: boolean;
}) => {
  const sourceFilter = instanceId
    ? field('rawlogs.dsi_id').str.equals(instanceId)
    : or(field('rawlogs.dsi_id').str.equals(''), field('rawlogs.dsi_id').str.empty());

  const workflowFilter = field("AVALOR_CONTEXT(rawlogs, 'workflowId')").str.equals(workflowId as string);

  const fieldTypesQuery: DwQueryRequest = {
    select: {
      dims: [
        {
          name: 'COLLECT_DISTINCT(DESCRIBE_JSON(rawlogs.document, true))',
          alias: 'fieldTypes',
        },
      ],
      metrics: [],
    },
    filter: and(sourceFilter, workflowFilter),
    projectionId: {
      name: 'rawlogs.limited',
      builtIn: true,
    },
    distinct: true,
    sorting: [],
  };

  const fieldKeysQuery: DwQueryRequest = {
    select: {
      dims: [
        {
          name: 'COLLECT_DISTINCT(EXPLODE(rawlogs.DOCUMENT_KEYS))',
          alias: 'fieldKeys',
        },
      ],
      metrics: [],
    },
    filter: and(sourceFilter, workflowFilter),
    projectionId: {
      name: 'rawlogs.limited',
      builtIn: true,
    },
    distinct: true,
    sorting: [],
  };

  const queryEnabled = enabled && !!workflowId;

  const { data: fieldTypesData, isLoading: isLoadingFieldTypes } = useQueryObject({
    queryObject: fieldTypesQuery,
    options: { enabled: queryEnabled, placeholderData: keepPreviousData },
    onSuccess: data => data?.[0].fieldTypes,
  });

  const { data: fieldKeysData, isLoading: isLoadingFieldKeys } = useQueryObject({
    queryObject: fieldKeysQuery,
    options: { enabled: queryEnabled, placeholderData: keepPreviousData },
    onSuccess: data => data?.[0].fieldKeys,
  });
  const fieldTypes = useMemo(() => fieldTypesData ?? [], [fieldTypesData]);
  const keysFromTypes = useMemo(() => fieldTypes?.map(({ path }) => path) || [], [fieldTypes]);
  // Raw paths as emitted by the transformer — V2 may include `[*]` markers and
  // backslash escapes. Preview columns need this form (so a formatter can walk
  // arrays). Downstream code that compares against mapping values (which live
  // in the un-escaped, un-marked space) should use `keys` instead.
  const rawKeys = useMemo(() => (fieldKeysData?.length ? fieldKeysData : keysFromTypes), [fieldKeysData, keysFromTypes]);
  // Dedup: during V1→V2 rollout a customer's DOCUMENT_KEYS can contain both
  // `foo.bar` (V1) and `foo[*].bar` (V2), which normalize to the same path.
  const keys = useMemo<string[]>(() => Array.from(new Set(rawKeys.map((k: string) => normalizePath(k)))), [rawKeys]);
  const fields = useMemo(
    () => buildKeyTree(rawKeys && rawKeys.length > 0 ? rawKeys : keysFromTypes, fieldTypes),
    [rawKeys, keysFromTypes, fieldTypes]
  );
  return useMemo(
    () => ({
      keys,
      rawKeys,
      fields,
      fieldTypesMap: fieldTypes.reduce(
        (acc, { path, type }) => {
          acc[path] = typeToFormatterResolver[type] || typeToFormatterResolver.Text;
          return acc;
        },
        {} as Record<string, string>
      ),
      hasLoaded: !!fieldTypesData && !!fieldKeysData,
      isLoading: isLoadingFieldTypes || isLoadingFieldKeys,
      isLoadingFieldTypes,
    }),
    [keys, rawKeys, fields, fieldTypes, fieldTypesData, fieldKeysData, isLoadingFieldTypes, isLoadingFieldKeys]
  );
};
